import { memo, useEffect, type ComponentType } from 'react';
import { getErrorStepConfig } from 'pages/AutomationEngine/steps';
import type { KizenNodeType, Step } from 'pages/AutomationEngine/types';
import type { FlowNodeType } from 'pages/AutomationEngine/Engine/Flow/types';
import { useAutomationsDispatch } from 'pages/AutomationEngine/store/react';
import { actions } from 'pages/AutomationEngine/store';
import { ERROR_COUNT_ACTIONS } from 'pages/AutomationEngine/store/consts';
import { StepCard } from './StepCard';
import MovingPlaceholder from './moving-placeholder/MovingPlaceholder';
import MovingGotoPlaceholder from './moving-placeholder/MovingGotoPlaceholder';
import MovingBranchPlaceholder from './moving-placeholder/MovingBranchPlaceholder';
import MovingGoalPlaceholder from './moving-placeholder/MovingGoalPlaceholder';
import MovingConditionPlaceholder from './moving-placeholder/MovingConditionPlaceholder';
import MovingLoopPlaceholder from './moving-placeholder/MovingLoopPlaceholder';

import * as cardTypes from './type';
import { StepCardOld } from './StepCardOld';
import { useTranslation } from 'react-i18next';

type ErrorStepCardProps = {
  type: FlowNodeType;
  step: Step;
  showStats?: boolean;
  disableMenu?: boolean;
  scale?: number | null;
  onStart?: (() => void) | null;
  onStop?: (() => void) | null;
  showMovingPlaceholder?: boolean;
  [key: string]: any;
};

function ErrorStepCard({
  type,
  step,
  showStats,
  disableMenu,
  scale,
  onStart,
  onStop,
  showMovingPlaceholder = false,
  showMovingBlocker = false,
  ...others
}: ErrorStepCardProps) {
  const { t } = useTranslation();
  const dispatch = useAutomationsDispatch();

  const stepType =
    step.type === 'goal' ? (step.details?.type as KizenNodeType) : step.type;

  // log the id in the error list (the reducer ignores ids already tracked)
  useEffect(() => {
    dispatch(
      actions.updateErrors({ id: step.id, action: ERROR_COUNT_ACTIONS.add })
    );
  }, [dispatch, step.id]);

  const stepConfig = getErrorStepConfig(stepType);

  // the lookup below spans every export of `./type`, whose prop types are
  // heterogeneous (and some closed), so the call site is typed structurally
  const CardComponent: ComponentType<any> =
    (showMovingPlaceholder && MovingPlaceholder) ||
    (showMovingBlocker === 'goto' && MovingGotoPlaceholder) ||
    (showMovingBlocker === 'branch' && MovingBranchPlaceholder) ||
    (showMovingBlocker === 'goal' && MovingGoalPlaceholder) ||
    (showMovingBlocker === 'condition' && MovingConditionPlaceholder) ||
    (showMovingBlocker === 'loop' && MovingLoopPlaceholder) ||
    cardTypes[stepConfig.CardComponent as keyof typeof cardTypes] ||
    (others.isSmartConnectorFlow ? StepCardOld : StepCard);

  const badgeProps = {
    color: stepConfig.color,
    bgColor: stepConfig.bgColor,
    label: stepConfig.label?.(t),
    icon: stepConfig.icon,
  };

  return (
    <CardComponent
      badgeProps={badgeProps}
      borderColor="var(--color-button-warning-hover)"
      panel={null}
      menuOptions={
        stepConfig.deletable ? [{ label: 'Delete', value: 'delete' }] : []
      }
      disableMenu={
        disableMenu || (!stepConfig.creatable && !stepConfig.deletable)
      }
      {...(stepConfig.CardComponent && { type, step })}
      {...(stepConfig.movable && {
        scale,
        onStart,
        onStop,
      })}
      {...others}
    />
  );
}

export default memo(ErrorStepCard);
